import {
	ActionIcon,
	Avatar,
	Badge,
	Button,
	Card,
	Divider,
	Drawer,
	Flex,
	Menu,
	Modal,
	ScrollArea,
	SimpleGrid,
	Skeleton,
	Loader,
	Stack,
	Text,
	TextInput,
	Textarea,
	UnstyledButton,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
	IconDotsVertical,
	IconDownload,
	IconEdit,
	IconEye,
	IconFileText,
	IconMail,
	IconMessageCircle,
	IconNote,
	IconTrash,
	IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	exportActiveUsersCsv,
	getActiveUserNotes,
	getActiveUsers,
	getActiveUsersOverview,
	sendActiveUserEmail,
} from 'src/apis/userActivityAPI'
import {
	CmsNote,
	createCmsNote,
	deleteCmsNote,
	getCmsNotes,
	updateCmsNote,
} from 'src/apis/cmsNoteAPI'
import {
	createSupportInboxCampaign,
	sendSupportInboxCampaign,
	streamSupportInboxCampaign,
} from 'src/apis/supportInboxAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate, size } from 'src/variable/constant'
import { formatLastActive } from './activeUserDisplay'
import {
	readActiveUserNotesFor,
	removeActiveUserNote,
} from './activeUserNotesStorage'
import {
	ActiveUserCardValue,
	ActiveUsersOverview,
	NO_OPEN_CATEGORY,
	activeDaysLabel,
	CARDS_ADD_UP_TEXT,
	cardDescription,
	cardCount,
	exportFileName,
	exportParams,
	fileNameFromDisposition,
	formatCount,
	formatWindowRange,
	isDeletedActiveUser,
	readBlobError,
	saveBlobAs,
} from './activeUserOverview'

type ActivityCategory =
	| 'highly_active'
	| 'active'
	| 'potential'
	| 'at_risk'
	| 'dormant'

interface ActiveUser {
	id: number
	user_id: string
	active_days: number
	total_sessions: number
	category: ActivityCategory | typeof NO_OPEN_CATEGORY
	updated_at?: string | null
	user?: {
		id?: string
		name?: string | null
		phone?: string | null
		email?: string | null
		avatar?: string | null
		address?: string | null
		address_local?: string | null
		invite_code?: string | null
		version?: string | null
		blocked_at?: string | number | null
		unblocked_at?: string | number | null
		is_verified?: string | boolean | null
		is_regular?: string | boolean | null
		created_at?: string | null
		country?: string | null
		country_code?: string | null
		online_time?: string | number | null
		deleted_at?: string | null
	} | null
}

type ActiveUserRow = ActiveUser

type ActiveUserSearchFilter = {
	field: string
	value: string | null
}

const SUPPORT_INBOX_TARGET_SELECTED = 'SELECTED'
const SUPPORT_INBOX_TERMINAL_EVENTS = ['completed', 'failed', 'cancelled']

const ACTIVE_USER_SEARCH_FIELDS = [
	{
		value: 'name',
		label: 'Name',
	},
	{
		value: 'old_name',
		label: 'Old Name',
	},
	{
		value: 'email',
		label: 'Email',
	},
	{
		value: 'phone',
		label: 'Phone',
	},
	{
		value: 'invite_code',
		label: 'Invite code',
	},
]

const DEFAULT_ACTIVE_USER_SEARCH_FILTER: ActiveUserSearchFilter = {
	field: ACTIVE_USER_SEARCH_FIELDS[0].value,
	value: null,
}

const CATEGORY_OPTIONS: Array<{
	value: ActiveUserCardValue
	label: string
	description: string
	color: string
}> = [
		{
			value: 'all',
			label: 'All',
			description: cardDescription('all'),
			color: '#1f7a44',
		},
		{
			value: 'highly_active',
			label: 'VIP Active',
			description: activeDaysLabel('highly_active'),
			color: '#238636',
		},
		{
			value: 'active',
			label: 'Active',
			description: activeDaysLabel('active'),
			color: '#ca8a04',
		},
		{
			value: 'potential',
			label: 'Potential',
			description: activeDaysLabel('potential'),
			color: '#ea580c',
		},
		{
			value: 'at_risk',
			label: 'At Risk',
			description: activeDaysLabel('at_risk'),
			color: '#dc2626',
		},
		{
			value: 'dormant',
			label: 'Dormant',
			description: cardDescription('dormant'),
			color: '#64748b',
		},
		{
			value: NO_OPEN_CATEGORY,
			label: 'No app open in 60 days',
			description: cardDescription(NO_OPEN_CATEGORY),
			color: '#7c3aed',
		},
	]

const formatNoteDate = (value: string) => {
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return '-'

	return new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	}).format(date)
}

const sortCmsNotes = (items: CmsNote[]) =>
	[...items].sort(
		(first, second) =>
			new Date(second.updated_at || second.created_at).getTime() -
			new Date(first.updated_at || first.created_at).getTime(),
	)

interface NotesPanelProps {
	user: ActiveUserRow
	notes: CmsNote[]
	loading: boolean
	value: string
	editingNoteId: string | null
	legacyCount: number
	uploadingLegacy: boolean
	onValueChange: (value: string) => void
	onSave: () => void
	onEdit: (note: CmsNote) => void
	onDelete: (note: CmsNote) => void
	onUploadLegacy: () => void
	onClose: () => void
}

function NotesPanel({
	user,
	notes,
	loading,
	value,
	editingNoteId,
	legacyCount,
	uploadingLegacy,
	onValueChange,
	onSave,
	onEdit,
	onDelete,
	onUploadLegacy,
	onClose,
}: NotesPanelProps) {
	const userName =
		user.user?.name?.trim() ||
		(isDeletedActiveUser(user) ? 'Deleted user' : 'Unknown user')

	return (
		<Stack spacing={0} sx={{ height: '100%' }}>
			<Flex align="center" justify="space-between" gap={12} p="md">
				<Stack spacing={0}>
					<Text fw={700} size="lg">
						Notes for {userName}
					</Text>
					<Text size="xs" color="dimmed">
						Saved on the server, every admin sees these notes
					</Text>
				</Stack>
				<ActionIcon
					variant="subtle"
					color="gray"
					onClick={onClose}
					aria-label="Close notes"
				>
					<IconX size={20} />
				</ActionIcon>
			</Flex>

			<Divider />

			<ScrollArea sx={{ flex: 1 }}>
				<Stack p="md" spacing="lg">
					{legacyCount > 0 && (
						<Card
							withBorder
							radius="md"
							p="md"
							data-testid="legacy-notes-banner"
							sx={{ background: '#fffbeb', borderColor: '#fde68a' }}
						>
							<Stack spacing="xs">
								<Text size="sm" fw={600}>
									{legacyCount === 1
										? '1 note is saved only in this browser'
										: `${legacyCount} notes are saved only in this browser`}
								</Text>
								<Text size="xs" color="dimmed">
									Save them to the server so every admin sees them. They are removed
									from this browser once saved.
								</Text>
								<Flex justify="flex-end">
									<Button
										size="xs"
										variant="default"
										loading={uploadingLegacy}
										onClick={onUploadLegacy}
									>
										Save to server
									</Button>
								</Flex>
							</Stack>
						</Card>
					)}
					<Card
						withBorder
						radius="md"
						p="md"
						sx={{ background: '#f7faf7', borderColor: '#dce9df' }}
					>
						<Stack spacing="sm">
							<Flex align="center" gap={8}>
								<IconEdit size={17} color={color.CmsPrimary} />
								<Text fw={700} size="sm" color={color.CmsPrimary}>
									{editingNoteId ? 'Edit Note' : 'Create Note'}
								</Text>
							</Flex>
							<Textarea
								value={value}
								onChange={(event) => onValueChange(event.currentTarget.value)}
								placeholder="Write a note..."
								minRows={4}
								autosize
								maxRows={8}
								styles={{
									input: {
										borderColor: '#d8e4da',
										background: '#ffffff',
									},
								}}
							/>
							<Flex justify="flex-end">
								<Button
									onClick={onSave}
									disabled={!value.trim()}
									sx={{
										background: color.CmsPrimary,
										'&:hover': {
											background: `${color.CmsPrimary} !important`,
										},
									}}
								>
									Done
								</Button>
							</Flex>
						</Stack>
					</Card>

					<Stack spacing="sm">
						<Text fw={700}>Recent Notes</Text>
						{loading ? (
							<Card withBorder radius="md" p="lg">
								<Stack align="center" spacing={8}>
									<Loader size="sm" color="green" />
									<Text size="sm" color="dimmed">
										Loading notes...
									</Text>
								</Stack>
							</Card>
						) : notes.length === 0 ? (
							<Card withBorder radius="md" p="lg">
								<Stack align="center" spacing={6}>
									<IconFileText size={28} color="#94a3b8" />
									<Text size="sm" fw={600}>
										No notes yet
									</Text>
									<Text size="xs" color="dimmed" align="center">
										Create the first note for this user.
									</Text>
								</Stack>
							</Card>
						) : (
							notes.map((note) => (
								<Card key={note.id} withBorder radius="md" p="md">
									<Stack spacing={10}>
										<Flex justify="space-between" align="flex-start" gap={12}>
											<Stack spacing={2}>
												<Text size="xs" fw={600} data-testid="note-author">
													{note.author_name?.trim() || 'Unknown admin'}
												</Text>
												<Text size="xs" color="dimmed" data-testid="note-time">
													{formatNoteDate(note.updated_at || note.created_at)}
												</Text>
												{note.updated_at !== note.created_at && (
													<Text size="xs" color="dimmed">
														Edited
													</Text>
												)}
											</Stack>
											{note.is_mine !== false && (
											<Menu shadow="md" width={140} withinPortal position="bottom-end">
												<Menu.Target>
													<ActionIcon
														variant="subtle"
														color="gray"
														aria-label="Note actions"
													>
														<IconDotsVertical size={17} />
													</ActionIcon>
												</Menu.Target>
												<Menu.Dropdown>
													<Menu.Item
														icon={<IconEdit size={15} />}
														onClick={() => onEdit(note)}
													>
														Edit
													</Menu.Item>
													<Menu.Item
														color="red"
														icon={<IconTrash size={15} />}
														onClick={() => onDelete(note)}
													>
														Delete
													</Menu.Item>
												</Menu.Dropdown>
											</Menu>
											)}
										</Flex>
										<Text
											size="sm"
											sx={{
												whiteSpace: 'pre-wrap',
												overflowWrap: 'anywhere',
												lineHeight: 1.55,
											}}
										>
											{note.content}
										</Text>
									</Stack>
								</Card>
							))
						)}
					</Stack>
				</Stack>
			</ScrollArea>
		</Stack>
	)
}

function ActiveUsersPage() {
	const navigate = useNavigate()
	const { openError, openModal } = useModal()
	const isMobileNotes = useMediaQuery('(max-width: 991px)')
	const [data, setData] = useState<ActiveUserRow[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [category, setCategory] = useState<ActiveUserCardValue>('all')
	const [overview, setOverview] = useState<ActiveUsersOverview | null>(null)
	const [exporting, setExporting] = useState(false)
	const [legacyNotes, setLegacyNotes] = useState<
		ReturnType<typeof readActiveUserNotesFor>
	>([])
	const [uploadingLegacy, setUploadingLegacy] = useState(false)
	const [selectedUser, setSelectedUser] = useState<ActiveUserRow | null>(null)
	const [notes, setNotes] = useState<CmsNote[]>([])
	const [notesLoading, setNotesLoading] = useState(false)
	const [noteValue, setNoteValue] = useState('')
	const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
	const [emailUser, setEmailUser] = useState<ActiveUserRow | null>(null)
	const [emailForm, setEmailForm] = useState({ subject: '', body: '' })
	const [sendingEmail, setSendingEmail] = useState(false)
	const [messageUser, setMessageUser] = useState<ActiveUserRow | null>(null)
	const [messageBody, setMessageBody] = useState('')
	const [sendingMessage, setSendingMessage] = useState(false)
	const [messageProgress, setMessageProgress] = useState<any>(null)
	const [searchFilter, setSearchFilter] =
		useState<ActiveUserSearchFilter | null>(
			DEFAULT_ACTIVE_USER_SEARCH_FILTER,
		)
	const [debouncedSearchFilter, setDebouncedSearchFilter] =
		useState<ActiveUserSearchFilter | null>(searchFilter)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const handleGetData = useCallback(async () => {
		setLoading(true)
		try {
			const offset = (pagination.pageIndex - 1) * pagination.pageSize
			const normalizedSearchFilter =
				debouncedSearchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER
			const res = await getActiveUsers({
				limit: pagination.pageSize,
				offset,
				...(category !== 'all' ? { category } : {}),
				...(normalizedSearchFilter.value?.trim()
					? {
						search_field:
							normalizedSearchFilter.field ||
							DEFAULT_ACTIVE_USER_SEARCH_FILTER.field,
						search_value: normalizedSearchFilter.value.trim(),
					}
					: {}),
			})
			const objects = res?.data?.results?.objects
			const rows = Array.isArray(objects?.rows) ? objects.rows : []
			const count = Number(objects?.count) || 0

			setData(
				rows.map((item: ActiveUser) => ({
					...item,
					active_days: Number(item?.active_days) || 0,
					total_sessions: Number(item?.total_sessions) || 0,
				})),
			)
			setTotal(count)
		} catch (error) {
			setData([])
			setTotal(0)
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [
		category,
		debouncedSearchFilter,
		openError,
		pagination.pageIndex,
		pagination.pageSize,
	])

	// UD-559: window dates, card numbers and total users. An old server has no such call, and the
	// page then shows no dates and no numbers instead of an error.
	const loadOverview = useCallback(async () => {
		try {
			const res = await getActiveUsersOverview()
			const object = res?.data?.results?.object
			setOverview(object && typeof object === 'object' ? object : null)
		} catch {
			setOverview(null)
		}
	}, [])

	const handleExport = useCallback(async () => {
		if (exporting) return

		const appliedSearch = debouncedSearchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER
		setExporting(true)
		try {
			const res = await exportActiveUsersCsv(
				exportParams(category, appliedSearch.field, appliedSearch.value),
			)
			saveBlobAs(
				res.data,
				fileNameFromDisposition(
					res.headers?.['content-disposition'],
					exportFileName(category),
				),
			)
		} catch (error) {
			openError(await readBlobError(error))
		} finally {
			setExporting(false)
		}
	}, [category, debouncedSearchFilter, exporting, openError])

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				id: 'no',
				header: 'No',
				size: size.zero,
				Cell: ({ row }: any) =>
					(pagination.pageIndex - 1) * pagination.pageSize + row.index + 1,
			},
			{
				accessorKey: 'user.name',
				header: 'Name',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					const deleted = isDeletedActiveUser(row.original)
					return (
						<Flex align="center" gap={6} wrap="nowrap">
							<CustomTooltip
								label={
									row.original.user?.name || (deleted ? 'Deleted user' : '-')
								}
								maxWidth={size.text}
							/>
							{deleted && (
								<Badge
									size="xs"
									variant="filled"
									color="gray"
									data-testid="deleted-badge"
								>
									Deleted
								</Badge>
							)}
						</Flex>
					)
				},
			},
			{
				accessorKey: 'user.avatar',
				header: 'Avatar',
				size: size.zero,
				Cell: ({ row }: any) => {
					return <Avatar src={row.original.user?.avatar || undefined} radius={50} />
				},
			},
			{
				accessorKey: 'active_days',
				header: 'Active',
				size: 145,
				Cell: ({ cell }: any) => (
					<Text fw={700} color={color.CmsPrimary}>
						{Number(cell.getValue() || 0).toLocaleString('en-US')}
					</Text>
				),
			},
			{
				accessorKey: 'total_sessions',
				header: 'Session',
				size: 140,
				Cell: ({ cell }: any) => (
					<Text fw={700} color={color.CmsPrimary}>
						{Number(cell.getValue() || 0).toLocaleString('en-US')}
					</Text>
				),
			},
			{
				accessorKey: 'user.address',
				header: 'GPS',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					return (
						<CustomTooltip
							label={row.original.user?.address || '-'}
							maxWidth={size.medium}
						/>
					)
				},
			},
			{
				accessorKey: 'user.created_at',
				header: 'Date Join',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => {
					const createdAt = row.original.user?.created_at
					return createdAt ? dayjs(createdAt).format(formatDate) : '-'
				},
			},
			{
				accessorKey: 'user.email',
				header: 'Email',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ row }: any) => (
					<CustomTooltip
						label={row.original.user?.email || '-'}
						maxWidth={size.text}
					/>
				),
			},
			// Temporarily hidden: Phone, Address Local, Invite code, Version,
			// Status, Block expires, Verified, Last Active
			// UD-559: the "No app open in 60 days" card is the one place that shows when the
			// account was last online, because Active and Session are 0 for every row there.
			...(category === NO_OPEN_CATEGORY
				? [
						{
							id: 'last_online',
							header: 'Last online',
							size: size.zero,
							Cell: ({ row }: any) => {
								const onlineTime = row.original.user?.online_time
								const label = formatLastActive(onlineTime)
								return label === '-' ? 'Never' : label
							},
						},
				  ]
				: []),
		],
		[category, pagination.pageIndex, pagination.pageSize],
	)

	const handleCategoryChange = useCallback(
		(nextCategory: ActiveUserCardValue) => {
			setCategory(nextCategory)
			setPagination((previous) => ({
				...previous,
				pageIndex: 1,
			}))
		},
		[],
	)

	const handleChangePage = useCallback((pageIndex: number) => {
		setPagination((previous) => ({
			...previous,
			pageIndex,
		}))
	}, [])

	const handleSearchFilterChange = useCallback(
		(value: string | null, key: keyof ActiveUserSearchFilter) => {
			setSearchFilter((previous) => ({
				...(previous || DEFAULT_ACTIVE_USER_SEARCH_FILTER),
				[key]:
					key === 'field'
						? value || ACTIVE_USER_SEARCH_FIELDS[0].value
						: value,
			}))
			setPagination((previous) => ({
				...previous,
				pageIndex: 1,
			}))
		},
		[],
	)

	const handleChangePageSize = useCallback((pageSize: string | null) => {
		setPagination({
			pageIndex: 1,
			pageSize: Number(pageSize) || 10,
		})
	}, [])

	const handleDetail = useCallback((row: any) => {
		const userId = row?.original?.user?.id || row?.original?.user_id
		if (!userId) return

		navigate(`/users/${userId}`)
	}, [navigate])

	const loadNotes = useCallback(
		async (userId: string) => {
			setNotesLoading(true)
			try {
				let res
				try {
					// UD-559: the notes of every admin, with who wrote each one.
					res = await getActiveUserNotes(userId)
				} catch (error: any) {
					if (error?.response?.status !== 404) throw error
					// An older server has no shared list: fall back to this admin's own notes.
					res = await getCmsNotes({
						limit: 100,
						offset: 0,
						target_type: 'active_user',
						target_id: userId,
						fields: '["$all"]',
					})
				}
				const rows = res?.data?.results?.objects?.rows
				setNotes(Array.isArray(rows) ? sortCmsNotes(rows) : [])
			} catch (error) {
				setNotes([])
				openError(error)
			} finally {
				setNotesLoading(false)
			}
		},
		[openError],
	)

	const handleOpenNotes = useCallback((user: ActiveUserRow) => {
		setSelectedUser(user)
		setNotes([])
		setNoteValue('')
		setEditingNoteId(null)
		setLegacyNotes(readActiveUserNotesFor(user.user_id))
		loadNotes(user.user_id)
	}, [loadNotes])

	const handleCloseNotes = useCallback(() => {
		setSelectedUser(null)
		setLegacyNotes([])
		setNotes([])
		setNoteValue('')
		setEditingNoteId(null)
	}, [])

	const handleSaveNote = useCallback(async () => {
		if (!selectedUser) return

		const content = noteValue.trim()
		if (!content) return

		setNotesLoading(true)
		try {
			if (editingNoteId) {
				await updateCmsNote(editingNoteId, { content })
			} else {
				await createCmsNote({
					content,
					target_type: 'active_user',
					target_id: selectedUser.user_id,
				})
			}
			setNoteValue('')
			setEditingNoteId(null)
			await loadNotes(selectedUser.user_id)
		} catch (error) {
			openError(error)
		} finally {
			setNotesLoading(false)
		}
	}, [
		editingNoteId,
		loadNotes,
		noteValue,
		openError,
		selectedUser,
	])

	// UD-559: notes that were written when they lived in the browser only. Each one is saved with
	// the same text, then forgotten by this browser; one that fails stays here for another try.
	const handleUploadLegacyNotes = useCallback(async () => {
		if (!selectedUser || uploadingLegacy) return

		setUploadingLegacy(true)
		try {
			for (const legacyNote of legacyNotes) {
				await createCmsNote({
					content: legacyNote.content,
					target_type: 'active_user',
					target_id: selectedUser.user_id,
				})
				removeActiveUserNote(selectedUser.user_id, legacyNote.id)
				setLegacyNotes((previous) =>
					previous.filter((item) => item.id !== legacyNote.id),
				)
			}
			await loadNotes(selectedUser.user_id)
		} catch (error) {
			openError(error)
		} finally {
			setUploadingLegacy(false)
		}
	}, [legacyNotes, loadNotes, openError, selectedUser, uploadingLegacy])

	const handleEditNote = useCallback((note: CmsNote) => {
		setEditingNoteId(note.id)
		setNoteValue(note.content)
	}, [])

	const handleDeleteNote = useCallback(
		(note: CmsNote) => {
			if (!selectedUser) return

			openModal({
				type: 'confirm',
				label: 'Delete this note?',
				handleConfirm: async () => {
					try {
						await deleteCmsNote(note.id)
						setNotes((previous) =>
							previous.filter((item) => item.id !== note.id),
						)
						await loadNotes(selectedUser.user_id)
					} catch (error) {
						openError(error)
						return
					}
					if (editingNoteId === note.id) {
						setEditingNoteId(null)
						setNoteValue('')
					}
				},
			})
		},
		[
			editingNoteId,
			loadNotes,
			openError,
			openModal,
			selectedUser,
		],
	)

	const handleOpenEmail = useCallback((user: ActiveUserRow) => {
		if (!user.user?.email) return

		setEmailUser(user)
		setEmailForm({ subject: '', body: '' })
	}, [])

	const handleCloseEmail = useCallback(() => {
		if (sendingEmail) return
		setEmailUser(null)
		setEmailForm({ subject: '', body: '' })
	}, [sendingEmail])

	const handleSendEmail = useCallback(async () => {
		if (!emailUser?.user_id) return

		const subject = emailForm.subject.trim()
		const body = emailForm.body.trim()
		if (!subject || !body) return

		setSendingEmail(true)
		try {
			await sendActiveUserEmail(emailUser.user_id, { subject, body })
			openModal({
				type: 'success',
				label: 'Email sent successfully',
			})
			setEmailUser(null)
			setEmailForm({ subject: '', body: '' })
		} catch (error) {
			openError(error)
		} finally {
			setSendingEmail(false)
		}
	}, [emailForm.body, emailForm.subject, emailUser, openError, openModal])

	const handleOpenMessage = useCallback((user: ActiveUserRow) => {
		if (!user.user_id) return

		setMessageUser(user)
		setMessageBody('')
		setMessageProgress(null)
	}, [])

	const handleCloseMessage = useCallback(() => {
		if (sendingMessage) return
		setMessageUser(null)
		setMessageBody('')
		setMessageProgress(null)
	}, [sendingMessage])

	const handleSendMessage = useCallback(async () => {
		if (!messageUser?.user_id) return

		const content = messageBody.trim()
		if (!content) return

		setSendingMessage(true)
		setMessageProgress(null)
		let terminalEvent = ''
		let terminalProgress: any = null
		try {
			const createRes = await createSupportInboxCampaign({
				content,
				target_type: SUPPORT_INBOX_TARGET_SELECTED,
				user_ids: [messageUser.user_id],
			})
			const campaign = createRes?.data?.results?.object
			await sendSupportInboxCampaign(campaign.id)
			setMessageProgress({
				id: campaign.id,
				status: 'QUEUED',
				total_count: campaign.total_count || 0,
				success_count: 0,
				failed_count: 0,
			})
			await streamSupportInboxCampaign(campaign.id, ({ event, data }) => {
				setMessageProgress(data)
				if (SUPPORT_INBOX_TERMINAL_EVENTS.includes(event)) {
					terminalEvent = event
					terminalProgress = data
				}
			})

			if (terminalEvent && terminalEvent !== 'completed') {
				throw new Error(
					terminalProgress?.error_message || 'Send message failed',
				)
			}

			openModal({
				type: 'success',
				label: 'Message sent successfully',
			})
			setMessageUser(null)
			setMessageBody('')
			setMessageProgress(null)
		} catch (error) {
			openError(error)
		} finally {
			setSendingMessage(false)
		}
	}, [messageBody, messageUser, openError, openModal])

	useEffect(() => {
		handleGetData()
	}, [handleGetData])

	useEffect(() => {
		loadOverview()
	}, [loadOverview])

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchFilter(
				searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER,
			)
		}, 500)

		return () => clearTimeout(timer)
	}, [searchFilter])

	const notesPanel = selectedUser ? (
		<NotesPanel
			user={selectedUser}
			notes={notes}
			loading={notesLoading}
			value={noteValue}
			editingNoteId={editingNoteId}
			legacyCount={legacyNotes.length}
			uploadingLegacy={uploadingLegacy}
			onValueChange={setNoteValue}
			onSave={handleSaveNote}
			onEdit={handleEditNote}
			onDelete={handleDeleteNote}
			onUploadLegacy={handleUploadLegacyNotes}
			onClose={handleCloseNotes}
		/>
	) : null

	const windowRange = formatWindowRange(
		overview?.window_start,
		overview?.window_end,
	)
	const totalUsersText = formatCount(overview?.total_users)

	const messageStatusText = messageProgress
		? `Status: ${messageProgress.status || '-'} | Sent: ${messageProgress.success_count || 0
		}/${messageProgress.total_count || 0} | Failed: ${messageProgress.failed_count || 0
		}`
		: null

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Flex justify="space-between" align="flex-end" gap={16} wrap="wrap">
				<Stack spacing={4}>
					<Flex align="baseline" gap={12} wrap="wrap">
						<Text fw={700} size="xl">
							Active Users
						</Text>
						{windowRange && (
							<Text
								fw={600}
								size="sm"
								color={color.CmsPrimary}
								data-testid="active-users-window"
							>
								{windowRange}
							</Text>
						)}
					</Flex>
					<Text color="dimmed" size="sm">
						User activity based on active days and app sessions in the rolling
						30-day window
					</Text>
				</Stack>
				<Flex align="flex-end" gap={24} wrap="wrap">
					{totalUsersText && (
						<Stack spacing={0} align="flex-end" data-testid="active-users-total">
							<Text size="xs" color="dimmed">
								Total users
							</Text>
							<Text fw={700} size="xl" lh={1.2}>
								{totalUsersText}
							</Text>
						</Stack>
					)}
					<CustomSelect
						label="Rows per page"
						value={String(pagination.pageSize)}
						onChange={handleChangePageSize}
						data={[
							{ value: '10', label: '10' },
							{ value: '20', label: '20' },
							{ value: '50', label: '50' },
						]}
						clearable={false}
						sx={{ minWidth: 130 }}
					/>
				</Flex>
			</Flex>

			<SimpleGrid
				cols={7}
				spacing="sm"
				breakpoints={[
					{ maxWidth: 1500, cols: 4 },
					{ maxWidth: 1000, cols: 3 },
					{ maxWidth: 768, cols: 2 },
					{ maxWidth: 480, cols: 1 },
				]}
			>
				{CATEGORY_OPTIONS.map((option) => {
					const selected = category === option.value
					const countText = formatCount(cardCount(overview, option.value))
					const noOpenRange =
						option.value === NO_OPEN_CATEGORY
							? formatWindowRange(
									overview?.no_open?.window_start,
									overview?.no_open?.window_end,
							  )
							: ''
					const description = noOpenRange
						? `No session in ${noOpenRange}`
						: option.description

					return (
						<UnstyledButton
							key={option.value}
							onClick={() => handleCategoryChange(option.value)}
							aria-pressed={selected}
						>
							<Card
								withBorder
								radius="md"
								p="md"
								sx={{
									height: '100%',
									borderColor: selected ? option.color : '#e5e7eb',
									backgroundColor: selected ? `${option.color}0D` : '#ffffff',
									transition:
										'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
									'&:hover': {
										borderColor: option.color,
										transform: 'translateY(-1px)',
									},
								}}
							>
								<Flex
									align="center"
									justify="space-between"
									gap="sm"
									sx={{ height: '100%', textAlign: 'left' }}
								>
									<Stack spacing={2} sx={{ minWidth: 0, flex: '1 1 auto' }}>
										<Flex align="center" gap={8}>
											<span
												aria-hidden="true"
												style={{
													width: 9,
													height: 9,
													borderRadius: '50%',
													background: option.color,
													flexShrink: 0,
												}}
											/>
											<Text fw={700} size="sm">
												{option.label}
											</Text>
										</Flex>
										<Text size="xs" color="dimmed">
											{description}
										</Text>
									</Stack>
									<Text
										fw={700}
										size="xl"
										lh={1.2}
										sx={{ flexShrink: 0, textAlign: 'right' }}
										data-testid={`card-count-${option.value}`}
									>
										{countText}
									</Text>
								</Flex>
							</Card>
						</UnstyledButton>
					)
				})}
			</SimpleGrid>

			<Text
				size="xs"
				color="dimmed"
				data-testid="active-users-cards-add-up"
			>
				{CARDS_ADD_UP_TEXT}
			</Text>

			<Flex align="stretch" gap="md">
				<Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
					<Flex gap={8} sx={{ flexWrap: 'wrap', marginBottom: 16 }}>
						<CustomSelect
							value={
								(searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER).field
							}
							data={ACTIVE_USER_SEARCH_FIELDS}
							onChange={(value) =>
								handleSearchFilterChange(value, 'field')
							}
						/>
						<CustomClearInput
							value={
								(searchFilter || DEFAULT_ACTIVE_USER_SEARCH_FILTER).value
							}
							onChange={(value) =>
								handleSearchFilterChange(value, 'value')
							}
							placeholder="Search"
						/>
						<CustomTooltip label="Every row of the selected card and search, not only this page">
							<Button
								variant="default"
								leftIcon={<IconDownload size={16} />}
								loading={exporting}
								disabled={loading || total === 0}
								onClick={handleExport}
								aria-label="Export CSV"
							>
								Export CSV
							</Button>
						</CustomTooltip>
					</Flex>
					{loading && data.length === 0 ? (
						<Stack spacing={8}>
							{Array.from({ length: Math.min(pagination.pageSize, 10) }).map(
								(_, index) => (
									<Skeleton key={index} height={56} radius="md" />
								),
							)}
						</Stack>
					) : (
						<TableComponent
							columns={columns}
							total={total}
							data={data}
							pagination={pagination}
							loading={loading}
							handleChangePage={handleChangePage}
							enableRowActions
							hideMoreActions
							isRowGrey={(row: any) => isDeletedActiveUser(row?.original)}
							actionColumnSize={172}
							_renderCustomActions={(row: any) => (
								<Flex gap={6} align="center" wrap="nowrap">
									<CustomTooltip
										label={
											isDeletedActiveUser(row.original)
												? 'Account deleted'
												: 'Send message'
										}
									>
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={
													!row.original.user_id ||
													isDeletedActiveUser(row.original)
												}
												onClick={() => handleOpenMessage(row.original)}
												aria-label="Send message"
											>
												<IconMessageCircle size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
									<CustomTooltip
										label={
											isDeletedActiveUser(row.original)
												? 'Account deleted'
												: row.original.user?.email
													? 'Send email'
													: 'Email unavailable'
										}
									>
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={
													!row.original.user?.email ||
													isDeletedActiveUser(row.original)
												}
												onClick={() => handleOpenEmail(row.original)}
												aria-label="Send email"
											>
												<IconMail size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
									<CustomTooltip label="Notes">
										<ActionIcon
											variant="light"
											color="green"
											onClick={() => handleOpenNotes(row.original)}
											aria-label={`Open notes for ${row.original.user?.name || 'user'
												}`}
										>
											<IconNote size={17} />
										</ActionIcon>
									</CustomTooltip>
									<CustomTooltip label="Detail">
										<span>
											<ActionIcon
												variant="light"
												color="green"
												disabled={
													!(row.original.user?.id || row.original.user_id)
												}
												onClick={() => handleDetail(row)}
												aria-label={`Open detail for ${row.original.user?.name || 'user'
													}`}
											>
												<IconEye size={17} />
											</ActionIcon>
										</span>
									</CustomTooltip>
								</Flex>
							)}
							syncQueryUrl={false}
						/>
					)}
				</Stack>

				{selectedUser && !isMobileNotes && (
					<Card
						withBorder
						radius="md"
						p={0}
						sx={{
							width: 340,
							minWidth: 340,
							height: 'calc(100vh - 190px)',
							minHeight: 560,
							position: 'sticky',
							top: 16,
							overflow: 'hidden',
						}}
					>
						{notesPanel}
					</Card>
				)}
			</Flex>

			<Drawer
				opened={Boolean(selectedUser && isMobileNotes)}
				onClose={handleCloseNotes}
				position="right"
				size="100%"
				withCloseButton={false}
				padding={0}
			>
				{isMobileNotes ? notesPanel : null}
			</Drawer>

			<Modal
				opened={Boolean(messageUser)}
				onClose={handleCloseMessage}
				title="Send Message"
				size="lg"
				closeOnClickOutside={!sendingMessage}
				closeOnEscape={!sendingMessage}
			>
				<Stack>
					<Stack spacing={2}>
						<Text size="sm" color="dimmed">
							To: {messageUser?.user?.name || 'Unknown user'}
						</Text>
						<Text size="xs" color="dimmed">
							{messageUser?.user?.phone || messageUser?.user?.email || '-'}
						</Text>
					</Stack>
					<Textarea
						label="Message"
						minRows={8}
						value={messageBody}
						onChange={(event) => setMessageBody(event.currentTarget.value)}
						disabled={sendingMessage}
					/>
					{messageStatusText && (
						<Text size="sm" color="dimmed">
							{messageStatusText}
						</Text>
					)}
					<Flex justify="flex-end" gap={8}>
						<Button
							variant="default"
							onClick={handleCloseMessage}
							disabled={sendingMessage}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSendMessage}
							loading={sendingMessage}
							disabled={!messageBody.trim()}
							sx={{
								background: color.CmsPrimary,
								'&:hover': {
									background: `${color.CmsPrimary} !important`,
								},
							}}
						>
							Send
						</Button>
					</Flex>
				</Stack>
			</Modal>

			<Modal
				opened={Boolean(emailUser)}
				onClose={handleCloseEmail}
				title="Send Email"
				size="lg"
				closeOnClickOutside={!sendingEmail}
				closeOnEscape={!sendingEmail}
			>
				<Stack>
					<Text size="sm" color="dimmed">
						To: {emailUser?.user?.email || '-'}
					</Text>
					<TextInput
						label="Subject"
						value={emailForm.subject}
						onChange={(event) => {
							const subject = event.currentTarget.value
							setEmailForm((previous) => ({
								...previous,
								subject,
							}))
						}}
						disabled={sendingEmail}
					/>
					<Textarea
						label="Body"
						minRows={8}
						value={emailForm.body}
						onChange={(event) => {
							const body = event.currentTarget.value
							setEmailForm((previous) => ({
								...previous,
								body,
							}))
						}}
						disabled={sendingEmail}
					/>
					<Flex justify="flex-end" gap={8}>
						<Button
							variant="default"
							onClick={handleCloseEmail}
							disabled={sendingEmail}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSendEmail}
							loading={sendingEmail}
							disabled={
								!emailForm.subject.trim() || !emailForm.body.trim()
							}
							sx={{
								background: color.CmsPrimary,
								'&:hover': {
									background: `${color.CmsPrimary} !important`,
								},
							}}
						>
							Send
						</Button>
					</Flex>
				</Stack>
			</Modal>
		</Stack>
	)
}

export default React.memo(ActiveUsersPage)
